// Cost detail editor — inline panel for one cost line item.
//
// Behaviour parity with the main app's products-cost detail-panel:
//   - the recurring / one-time choice is IMMUTABLE once the row exists
//     (shown as a read-only badge);
//   - growth-formula chips come from the copied `FORMULA_PRESETS`;
//   - a unit-based toggle appears when the driver carries a `unit_label`;
//   - a live annualised summary;
//   - Save / Delete; Esc-to-close via a React key handler (not a document
//     capture-phase listener).

import { useEffect, useRef, useState } from 'react';
import { Icon } from '../Icon';
import type {
  PortalCostCreateRequest,
  PortalCostDriver,
  PortalCostItem,
  PortalCostUpdateRequest,
} from '../../api/types';
import { FORMULA_PRESETS, generateFormula, detectFormulaPreset } from '../../lib/financial/formulas';
import { intervalLabel, periodsPerYear, RECURRING_INTERVALS } from './helpers';

interface CostDetailPanelProps {
  /** The driver this row is for (chosen on add, fixed on edit). */
  driver: PortalCostDriver;
  /** Existing item when editing; null when adding a new row. */
  item: PortalCostItem | null;
  formatCurrency: (value: number | null | undefined) => string;
  onSaveCreate: (body: PortalCostCreateRequest) => Promise<void>;
  onSaveUpdate: (costId: string, body: PortalCostUpdateRequest) => Promise<void>;
  onDelete: (costId: string) => Promise<void>;
  onClose: () => void;
}

const PRESET_KEYS = Object.keys(FORMULA_PRESETS);

export function CostDetailPanel({
  driver,
  item,
  formatCurrency,
  onSaveCreate,
  onSaveUpdate,
  onDelete,
  onClose,
}: CostDetailPanelProps) {
  const isEdit = item != null;
  // Recurring is immutable once set: on edit it comes from the item; on create
  // it defaults to the driver's recurring flag and is chosen before the row
  // exists.
  const [recurring, setRecurring] = useState<boolean>(
    item?.recurring ?? driver.recurring ?? false,
  );
  const [amount, setAmount] = useState<string>(
    item?.cost != null ? String(item.cost) : '',
  );
  const [interval, setInterval] = useState<number>(item?.recurring_interval ?? 5);
  const [unitBased, setUnitBased] = useState<boolean>(item?.units != null);
  const [units, setUnits] = useState<string>(
    item?.units != null ? String(item.units) : '',
  );
  const [preset, setPreset] = useState<string>(
    detectFormulaPreset(item?.formula).preset ?? 'fixed',
  );
  const [saving, setSaving] = useState(false);
  const firstFieldRef = useRef<HTMLInputElement | null>(null);

  // Move focus inside on open; restore to the trigger on close is handled by
  // the parent re-rendering the accordion (the panel is conditionally mounted).
  useEffect(() => {
    firstFieldRef.current?.focus();
  }, []);

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
    }
  }

  const amountNum = amount ? Number(amount) : 0;
  const annual = recurring ? amountNum * periodsPerYear(interval) : amountNum;
  const hasUnitLabel = Boolean(driver.unit_label);

  async function handleSave() {
    setSaving(true);
    try {
      const formula =
        preset === 'fixed' || preset === 'custom'
          ? null
          : generateFormula(preset, FORMULA_PRESETS[preset]?.defaultRate ?? null);
      const cost = amount ? Number(amount) : null;
      const unitsValue = unitBased && units ? Number(units) : null;
      if (isEdit && item) {
        const body: PortalCostUpdateRequest = {
          cost,
          recurring_interval: recurring ? interval : null,
          formula,
          units: unitsValue,
        };
        await onSaveUpdate(item.id, body);
      } else {
        const body: PortalCostCreateRequest = {
          costdriver_id: driver.id,
          cost,
          recurring,
          recurring_interval: recurring ? interval : null,
          formula,
          units: unitsValue,
        };
        await onSaveCreate(body);
      }
      onClose();
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!item) return;
    setSaving(true);
    try {
      await onDelete(item.id);
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-label={`Edit cost — ${driver.name ?? 'driver'}`}
      onKeyDown={handleKeyDown}
      className="rounded-[10px] border-[1.5px] border-accent bg-white p-4 shadow-[0_4px_16px_rgba(0,0,0,0.08)]"
    >
      <div className="mb-3 flex items-center justify-between">
        <div className="text-sm font-bold text-ink">{driver.name ?? 'Cost driver'}</div>
        {isEdit ? (
          <span
            className="rounded-full border border-line bg-panel px-2 py-0.5 text-[0.66rem] font-bold uppercase tracking-wide text-muted"
            aria-label={recurring ? 'Recurring cost (fixed)' : 'One-time cost (fixed)'}
          >
            {recurring ? 'Recurring' : 'One-time'}
          </span>
        ) : (
          <div className="flex gap-1" role="group" aria-label="Cost type">
            <button
              onClick={() => setRecurring(false)}
              aria-pressed={!recurring}
              className={`rounded-lg border-[1.5px] px-2 py-0.5 text-[0.66rem] font-bold uppercase tracking-wide ${
                !recurring ? 'border-accent bg-accent-light text-accent' : 'border-line bg-white text-muted'
              }`}
            >
              One-time
            </button>
            <button
              onClick={() => setRecurring(true)}
              aria-pressed={recurring}
              className={`rounded-lg border-[1.5px] px-2 py-0.5 text-[0.66rem] font-bold uppercase tracking-wide ${
                recurring ? 'border-accent bg-accent-light text-accent' : 'border-line bg-white text-muted'
              }`}
            >
              Recurring
            </button>
          </div>
        )}
      </div>

      <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-muted">
        Amount {recurring ? `(per ${intervalLabel(interval).toLowerCase()})` : ''}
      </label>
      <input
        ref={firstFieldRef}
        type="number"
        min="0"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        className="mb-3 w-full rounded-[9px] border-[1.5px] border-line bg-white px-3 py-2 text-sm text-ink outline-none focus:border-accent"
      />

      {recurring && (
        <div className="mb-3">
          <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-muted">
            Interval
          </label>
          <select
            value={interval}
            onChange={(e) => setInterval(Number(e.target.value))}
            className="w-full rounded-[9px] border-[1.5px] border-line bg-white px-3 py-2 text-sm text-ink outline-none focus:border-accent"
          >
            {Object.entries(RECURRING_INTERVALS).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="mb-3">
        <div className="mb-1.5 text-xs font-bold uppercase tracking-wide text-muted">Growth</div>
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Growth formula">
          {PRESET_KEYS.map((key) => {
            const on = preset === key;
            return (
              <button
                key={key}
                onClick={() => setPreset(key)}
                className={`rounded-lg border-[1.5px] px-2.5 py-1 text-[0.72rem] font-semibold ${
                  on
                    ? 'border-accent bg-accent-light text-accent'
                    : 'border-line bg-white text-muted hover:border-accent hover:text-accent'
                }`}
              >
                {FORMULA_PRESETS[key].label}
              </button>
            );
          })}
        </div>
      </div>

      {hasUnitLabel && (
        <div className="mb-3">
          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={unitBased}
              onChange={(e) => setUnitBased(e.target.checked)}
            />
            Unit-based ({driver.unit_label})
          </label>
          {unitBased && (
            <input
              type="number"
              min="0"
              value={units}
              onChange={(e) => setUnits(e.target.value)}
              placeholder={`Number of ${driver.unit_label}`}
              className="mt-2 w-full rounded-[9px] border-[1.5px] border-line bg-white px-3 py-2 text-sm text-ink outline-none focus:border-accent"
            />
          )}
        </div>
      )}

      <div className="mb-3 rounded-[9px] bg-panel px-3 py-2 text-xs text-muted">
        Annualised: <span className="font-bold text-ink">{formatCurrency(annual)}</span>
      </div>

      <div className="flex items-center justify-between">
        {isEdit ? (
          <button
            onClick={handleDelete}
            disabled={saving}
            className="inline-flex items-center gap-1 rounded-[9px] border-[1.5px] border-line bg-white px-3 py-1.5 text-[0.79rem] font-semibold text-danger hover:border-danger disabled:opacity-60"
            aria-label="Delete cost"
          >
            <Icon name="closeXs" size={11} /> Delete
          </button>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <button
            onClick={onClose}
            disabled={saving}
            className="rounded-[9px] border-[1.5px] border-line bg-white px-3 py-1.5 text-[0.79rem] font-semibold text-ink hover:border-accent hover:text-accent disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-[9px] bg-accent px-3 py-1.5 text-[0.79rem] font-semibold text-white hover:bg-accent-hover disabled:opacity-60"
          >
            <Icon name="saveSm" size={12} /> {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
